import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Ticket } from "lucide-react";

import { listTicketProducts } from "@/lib/tickets.functions";

function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
}

function scoreSlug(attempt: string, slug: string): number {
  const a = attempt.toLowerCase();
  const s = slug.toLowerCase();
  if (s === a) return -1000;
  let score = levenshtein(a, s);
  if (s.includes(a) || a.includes(s)) score -= 5;
  // shared word bonus
  const aw = new Set(a.split("-").filter(Boolean));
  const sw = s.split("-").filter(Boolean);
  for (const w of sw) if (aw.has(w)) score -= 2;
  return score;
}

export function TicketNotFound({ attemptedSlug }: { attemptedSlug?: string }) {
  const fetchProducts = useServerFn(listTicketProducts);
  const { data, isLoading } = useQuery({
    queryKey: ["ticket-products-all"],
    queryFn: () => fetchProducts(),
  });

  const products = data?.products ?? [];
  const suggestions = attemptedSlug
    ? [...products]
        .map((p) => ({ p, score: scoreSlug(attemptedSlug, p.slug) }))
        .sort((a, b) => a.score - b.score)
        .slice(0, 3)
        .map((x) => x.p)
    : products.slice(0, 3);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-foreground font-mono text-[9px] uppercase tracking-[0.22em]">
        <Ticket className="size-3" /> ticket not found
      </div>
      <h1 className="mt-4 text-4xl sm:text-5xl font-display font-extrabold lowercase leading-tight">
        we couldn't find that ticket
      </h1>
      {attemptedSlug ? (
        <p className="mt-3 text-sm text-muted-foreground">
          no ticket matches{" "}
          <code className="px-1.5 py-0.5 rounded bg-secondary font-mono text-foreground">
            /tickets/{attemptedSlug}
          </code>
          . here are the closest matches:
        </p>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">
          this link doesn't match any active ticket. try one of these:
        </p>
      )}

      <div className="mt-8 space-y-3">
        {isLoading && (
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">loading…</p>
        )}
        {!isLoading && suggestions.length === 0 && (
          <p className="text-sm text-muted-foreground">no active tickets right now.</p>
        )}
        {suggestions.map((p) => (
          <Link
            key={p.slug}
            to="/tickets/$slug"
            params={{ slug: p.slug }}
            className="group flex items-center justify-between gap-4 rounded-2xl border border-border bg-card hover:bg-secondary transition-colors p-4"
          >
            <div className="min-w-0">
              <div className="font-bold lowercase truncate">{p.name}</div>
              <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate">
                /tickets/{p.slug}
              </div>
            </div>
            <ArrowRight className="size-4 shrink-0 transition-transform group-hover:translate-x-1" />
          </Link>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link
          to="/tickets"
          className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-3 text-[10px] font-bold uppercase tracking-[0.22em] text-background hover:bg-accent transition-colors"
        >
          <ArrowLeft className="size-3.5" /> all tickets
        </Link>
        <Link
          to="/"
          className="inline-flex items-center rounded-full border border-border px-5 py-3 text-[10px] font-bold uppercase tracking-[0.22em] hover:bg-secondary transition-colors"
        >
          back home
        </Link>
      </div>
    </div>
  );
}
